import { useMemo, type ReactNode } from 'react';
import { Add, Cross } from '@/components/icons';
import TestTableEditor from '@/components/preview/test-table-editor/TestTableEditor';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { DEFAULT_TABLE_PRIMARY_COLOR } from '@/constants';
import { isClassificationColoringOn } from '@/lib/classification-color';
import { createTableBlockFromPublisherResult } from '@/lib/publisher-result-imports';
import { resolveTableHeaderColor } from '@/lib/table-utils';
import { classificationNote, tableLabel } from './guided-review-model';
import type {
    PublisherResultImport,
    PublisherResultTableEntry,
} from '@/types/publisher-result-import';
import type { ReportData } from '@/types/report';

/**
 * The extracted tables, rendered the way the laudo will render them.
 *
 * A step is meant to be a rehearsal of the report block, so the grid here is
 * the report's own editor in read-only mode rather than a summary of it — the
 * psychologist compares this against the PDF, and a second rendering of the
 * same numbers would be one more thing that can disagree.
 */

export function TablePreview({
    entry,
    record,
    reportData,
}: {
    entry: PublisherResultTableEntry;
    record: PublisherResultImport;
    reportData?: ReportData;
}) {
    const block = useMemo(
        () =>
            createTableBlockFromPublisherResult({
                table: entry.table.table,
                order: 0,
                description: tableLabel(entry),
                sourceImportId: record.id,
                sourceInstrumentResultId: entry.instrument.id,
                sourceImportTableId: entry.table.id,
            }),
        [entry, record.id],
    );

    return (
        // No `pointer-events-none` here, and no `overflow-x-auto`: the grid
        // brings its own scroll container, and killing pointer events on an
        // ancestor takes that scroller down with it — a wide table would paint
        // a scrollbar nobody could grab. `disabled` is what makes the rehearsal
        // read-only.
        <div className="force-light rounded-xl bg-card">
            <TestTableEditor
                block={block}
                onUpdate={() => {}}
                primaryColor={
                    reportData?.customizations?.colors?.primary || DEFAULT_TABLE_PRIMARY_COLOR
                }
                headerColor={resolveTableHeaderColor(
                    reportData?.customizations?.table?.header_color,
                    reportData?.customizations?.colors?.primary,
                )}
                textAlignment={reportData?.customizations?.table?.text_alignment || 'center'}
                colorClassifications={isClassificationColoringOn(reportData?.customizations?.table)}
                disabled
            />
        </div>
    );
}

/**
 * One included table, with the label the block carries into the laudo and the
 * one way out of including it.
 *
 * The paragraph written about the table goes in `children`, inside this same
 * card. They used to be two cards stacked, which read as two blocks that had
 * landed next to each other by accident — but the paragraph is *about* the
 * numbers above it, and in the laudo the two arrive together. One card, with a
 * rule between them, says that.
 */
export function TableCard({
    entry,
    record,
    reportData,
    onExclude,
    children,
}: {
    entry: PublisherResultTableEntry;
    record: PublisherResultImport;
    reportData?: ReportData;
    onExclude: () => void;
    children?: ReactNode;
}) {
    const provenance = classificationNote(entry);

    return (
        <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-foreground">{tableLabel(entry)}</span>
                <TooltipIconButton
                    label="Não incluir no laudo"
                    onClick={onExclude}
                    className="text-muted-foreground hover:text-foreground"
                >
                    <Cross className="size-4" />
                </TooltipIconButton>
            </div>
            <TablePreview entry={entry} record={record} reportData={reportData} />
            {provenance && (
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{provenance}</p>
            )}
            {children}
        </div>
    );
}

/**
 * The tables of this instrument the curator left out.
 *
 * Behind a disclosure because leaving them out is an answer already given, not
 * a question — but one click from being reversed, because it is sometimes the
 * wrong answer.
 */
export function ExcludedTables({
    entries,
    onInclude,
}: {
    entries: PublisherResultTableEntry[];
    onInclude: (tableId: string) => void;
}) {
    if (entries.length === 0) return null;

    return (
        <details className="rounded-2xl border border-border bg-surface/60 p-3">
            <summary className="cursor-pointer text-sm text-muted-foreground">
                {entries.length === 1
                    ? '1 tabela não entra no laudo'
                    : `${entries.length} tabelas não entram no laudo`}
            </summary>
            <ul className="mt-3 space-y-2">
                {entries.map((entry) => (
                    <li
                        key={entry.table.id}
                        className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2"
                    >
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                            {tableLabel(entry)}
                        </span>
                        <TooltipIconButton
                            label="Incluir no laudo"
                            onClick={() => onInclude(entry.table.id)}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            <Add className="size-4" />
                        </TooltipIconButton>
                    </li>
                ))}
            </ul>
        </details>
    );
}
